import React from 'react';
import { useForm } from "./provider";
import { PlusIcon, BanknotesIcon } from '@heroicons/react/24/outline';

export const PickPaymentMethodPage = (props) => {
  const { state } = useForm();
  const { offline_payments_enabled } = state;

  return (
    <div className="p-6">
      {/* Section Title */}
      <p className="text-sm font-medium text-gray-700 mb-4">Select a payment method</p>

      {/* Payment Options */}
      <div className="space-y-3">
        {/* Existing Cards */}
        {state.paymentMethods.length > 0 && (
          <div className="space-y-2">
            {state.paymentMethods.map((paymentMethod, index) => (
              <Card key={index} paymentMethod={paymentMethod} goToNamedStep={props.goToNamedStep} />
            ))}
          </div>
        )}

        {/* Divider if there are saved cards */}
        {state.paymentMethods.length > 0 && (
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-gray-400 uppercase tracking-wide">or</span>
            </div>
          </div>
        )}

        {/* Other Options */}
        <div className="space-y-2">
          <CreateNewCard goToNamedStep={props.goToNamedStep} />
          {offline_payments_enabled && <OfflineCard goToNamedStep={props.goToNamedStep} />}
        </div>
      </div>
    </div>
  );
};

const OfflineCard = ({ goToNamedStep }) => {
  const { dispatch } = useForm();

  const handleClick = () => {
    dispatch({ type: "SET_OFFLINE_PAYMENT", offlinePayment: true });
    goToNamedStep("offline_payment_page");
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50 transition-all duration-200 text-left group"
    >
      <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
        <BanknotesIcon className="w-6 h-6 text-amber-600" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">Record Offline Payment</p>
        <p className="text-xs text-gray-500">Check, cash, or money order</p>
      </div>
      <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};

const CreateNewCard = ({ goToNamedStep }) => {
  return (
    <button
      type="button"
      onClick={() => goToNamedStep("create_payment_method_page")}
      className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-200 text-left group"
    >
      <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
        <PlusIcon className="w-6 h-6 text-indigo-600" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">Add New Card</p>
        <p className="text-xs text-gray-500">Enter new card details</p>
      </div>
      <svg className="w-5 h-5 text-gray-400 group-hover:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};

const Card = ({ paymentMethod, goToNamedStep }) => {
  const { card } = paymentMethod;
  const { setPaymentMethod } = useForm();

  const handleSubmit = () => {
    setPaymentMethod(paymentMethod);
    goToNamedStep("card_payment_page");
  };

  // Brand colors and icons
  const brandConfig = {
    visa: { gradient: 'from-blue-600 to-blue-800', label: 'VISA' },
    mastercard: { gradient: 'from-red-500 to-orange-500', label: 'MC' },
    amex: { gradient: 'from-blue-400 to-blue-600', label: 'AMEX' },
    discover: { gradient: 'from-orange-400 to-orange-600', label: 'DISC' },
  };
  const config = brandConfig[card.brand.toLowerCase()] || { gradient: 'from-gray-600 to-gray-800', label: card.brand.slice(0, 4).toUpperCase() };

  return (
    <button
      type="button"
      onClick={handleSubmit}
      className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50 transition-all duration-200 text-left group"
    >
      {/* Card Visual */}
      <div className={`w-14 h-9 rounded-md bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-sm`}>
        <span className="text-[10px] font-bold text-white tracking-wider">{config.label}</span>
      </div>

      {/* Card Details */}
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">•••• {card.last4}</p>
        <p className="text-xs text-gray-500">Expires {card.exp_month}/{card.exp_year}</p>
      </div>

      {/* Arrow */}
      <svg className="w-5 h-5 text-gray-400 group-hover:text-emerald-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};
